/**
 * Listening-rule query helpers.
 *
 * Shared dialect with ff-api `admin/model/marketing_ops/listening_query.py`:
 * create/update validate the same structure; ingest re-evaluates candidates
 * locally after SERP discover (providers disagree on boolean syntax).
 *
 * Dialect (case-insensitive operators):
 * - Terms: bare words, #hashtags, @handles, "quoted phrases"
 * - Author feed: from:@handle (ingest hint — posts by that account, not mentions of it)
 * - Boolean: AND, OR, NOT
 * - Grouping: ( … )
 * - CONTAINS term|"phrase" — substring match
 *
 * Simple mode only authors flat OR/AND lists + a separate exclude field.
 * Advanced mode authors the full dialect in one include string (and optional exclude).
 */

export type MatchMode = 'any' | 'all';
export type QueryEditorMode = 'simple' | 'advanced';

const OP = /^(AND|OR|NOT|CONTAINS)$/i;

/** `from:@handle` is one field term, not a bare word that needs quoting. */
export function isFromFieldTerm(token: string): boolean {
  return /^from:/i.test(token.trim());
}

/** Serialize chip terms into a flat boolean query. */
export function formatChipQuery(terms: string[], mode: MatchMode): string {
  const joiner = mode === 'all' ? ' AND ' : ' OR ';
  return terms
    .map((t) => {
      const cleaned = t.replace(/"/g, '').trim();
      if (!cleaned) return '';
      // Field operator — posts authored by handle (listen-target feed).
      if (isFromFieldTerm(cleaned)) return cleaned;
      if (cleaned.startsWith('#') || cleaned.startsWith('@')) return cleaned;
      return `"${cleaned}"`;
    })
    .filter(Boolean)
    .join(joiner);
}

/**
 * Tokenize for validation / simple-mode detection.
 * Keeps quoted strings intact; uppercases known operators for comparison.
 */
export function tokenizeListeningQuery(raw: string): string[] {
  const tokens: string[] = [];
  const re = /"([^"\\]|\\.)*"|[()]|[^\s"()]+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    tokens.push(m[0]);
  }
  return tokens;
}

export type ListeningQueryIssue = {
  message: string;
};

/** Light structural validation — not a full parser for every provider quirk. */
export function validateListeningQuery(raw: string): ListeningQueryIssue | null {
  const q = raw.trim();
  if (!q) return { message: 'Enter a query' };

  let depth = 0;
  for (const ch of q) {
    if (ch === '(') depth += 1;
    if (ch === ')') depth -= 1;
    if (depth < 0) return { message: 'Unmatched closing parenthesis' };
  }
  if (depth !== 0) return { message: 'Unmatched opening parenthesis' };

  const tokens = tokenizeListeningQuery(q);
  if (tokens.length === 0) return { message: 'Enter a query' };

  const isOp = (t: string) => OP.test(t);
  const isAndOr = (t: string) => /^(AND|OR)$/i.test(t);
  const isTermLike = (t: string) => t === '(' || t === ')' || !isOp(t);

  if (isAndOr(tokens[0])) {
    return { message: `Query cannot start with ${tokens[0].toUpperCase()}` };
  }
  if (isAndOr(tokens[tokens.length - 1])) {
    return { message: `Query cannot end with ${tokens[tokens.length - 1].toUpperCase()}` };
  }

  for (let i = 0; i < tokens.length; i++) {
    const cur = tokens[i];
    const next = tokens[i + 1];

    if (/^CONTAINS$/i.test(cur)) {
      if (!next || isOp(next) || next === ')' || next === '(') {
        return { message: 'CONTAINS needs a term or "phrase" after it' };
      }
    }

    if (/^NOT$/i.test(cur)) {
      if (!next || isAndOr(next) || next === ')') {
        return { message: 'NOT needs a term or group after it' };
      }
    }

    if (isAndOr(cur) && next && isAndOr(next)) {
      return { message: `Cannot place ${cur.toUpperCase()} next to ${next.toUpperCase()}` };
    }

    if (cur === '(' && next === ')') {
      return { message: 'Empty parentheses are not allowed' };
    }

    if (isTermLike(cur) && cur !== '(' && cur !== ')' && next && isTermLike(next) && next !== '(' && next !== ')') {
      // Two bare terms in a row without an operator — ambiguous.
      if (!isOp(cur) && !isOp(next)) {
        return {
          message: `Add AND or OR between ${cur} and ${next}`,
        };
      }
    }
  }

  return null;
}

/**
 * Detect flat chip-compatible queries: term (AND|OR term)* with one joiner type.
 * Returns null when nested / NOT / CONTAINS / mixed joiners require Advanced mode.
 */
export function tryParseFlatQuery(
  raw: string,
): { terms: string[]; matchMode: MatchMode } | null {
  const q = raw.trim();
  if (!q) return { terms: [], matchMode: 'any' };
  if (/[()]/.test(q) || /\bNOT\b/i.test(q) || /\bCONTAINS\b/i.test(q)) return null;

  const tokens = tokenizeListeningQuery(q);
  if (tokens.length === 0) return { terms: [], matchMode: 'any' };

  const terms: string[] = [];
  let mode: MatchMode | null = null;

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (/^(AND|OR)$/i.test(t)) {
      const nextMode: MatchMode = /^AND$/i.test(t) ? 'all' : 'any';
      if (mode && mode !== nextMode) return null;
      mode = nextMode;
      continue;
    }
    if (OP.test(t) || t === '(' || t === ')') return null;
    terms.push(unwrapTerm(t));
  }

  if (terms.length === 0) return null;
  return { terms, matchMode: mode ?? 'any' };
}

function unwrapTerm(token: string): string {
  if (token.startsWith('"') && token.endsWith('"') && token.length >= 2) {
    return token.slice(1, -1).replace(/\\"/g, '"');
  }
  return token;
}

/** Operator snippets for the advanced editor insert bar. */
export const LISTENING_QUERY_INSERTS: { label: string; insert: string; tip: string }[] = [
  { label: 'AND', insert: ' AND ', tip: 'Both sides must match' },
  { label: 'OR', insert: ' OR ', tip: 'Either side may match' },
  { label: 'NOT', insert: ' NOT ', tip: 'Exclude the next term or group' },
  { label: 'CONTAINS', insert: ' CONTAINS ', tip: 'Substring match on the next term' },
  { label: 'from:', insert: 'from:@', tip: 'Posts authored by this handle (listen-target feed)' },
  { label: '( )', insert: '(  )', tip: 'Group terms — cursor lands inside' },
  { label: '" "', insert: '" "', tip: 'Exact phrase — cursor lands inside' },
];

export const LISTENING_QUERY_EXAMPLES = [
  'Fairful OR @fairful OR "Fairful app"',
  'from:@rivalstay',
  '(from:@rivalstay) AND (Fairful OR @fairful)',
  '"summer stays" OR #FairfulSummer',
  '(Fairful AND (scam OR unsafe OR fraud))',
  'Fairful AND CONTAINS "host guarantee"',
] as const;
